import React from 'react';
import { Swords, Footprints, Shield, HelpCircle, Target, Zap, Eye, Heart, Flame, AlertCircle, CircleSlash } from 'lucide-react';
import './ActionIcon.css';

/**
 * Componente reutilizable para mostrar iconos de acciones
 * Puede recibir un ID de acción o un objeto acción completo
 */
const ActionIcon = ({ 
  actionId, 
  action, 
  size = 16, 
  className = '', 
  showFallback = true,
  teamClass = null // ✅ Nueva prop para el equipo
}) => {
  
  // Determinar el slug de la acción
  const actionSlug = action?.slug || actionId;
  
  // ✅ Crear clase dinámica para equipos con prefijo action-
  const teamClassSuffix = teamClass ? ` action-team-${teamClass}` : '';
  
  // Mapeo de iconos por slug de acción
  const actionIcons = {
    // Movimiento
    'move': <Footprints size={size} className={`action-icon-move${teamClassSuffix}`} />,
    'light-move': <Footprints size={size} className={`action-icon-light-move${teamClassSuffix}`} />,
    'heavy-move': <Footprints size={size} className={`action-icon-heavy-move${teamClassSuffix}`} />,

    // Ataques cuerpo a cuerpo
    'sword-attack': <Swords size={size} className={`action-icon-melee${teamClassSuffix}`} />,
    'light-attack': <Swords size={size} className={`action-icon-light-attack${teamClassSuffix}`} />,
    'heavy-attack': <Swords size={size} className={`action-icon-heavy-attack${teamClassSuffix}`} />,

    // Ataques a distancia (flechas)
    'range-attack': <Target size={size} className={`action-icon-range${teamClassSuffix}`} />,
    'ranged-attack': <Target size={size} className={`action-icon-ranged${teamClassSuffix}`} />,
    'bow-attack': <Target size={size} className={`action-icon-bow${teamClassSuffix}`} />,
    'crossbow-attack': <Target size={size} className={`action-icon-crossbow${teamClassSuffix}`} />,
    'basic-arrow': <Target size={size} className={`action-icon-basic-arrow${teamClassSuffix}`} />,
    'poison-arrow': <Target size={size} className={`action-icon-poison-arrow${teamClassSuffix}`} />,
    'acid-arrow': <Target size={size} className={`action-icon-acid-arrow${teamClassSuffix}`} />,
    'volley': <Target size={size} className={`action-icon-volley${teamClassSuffix}`} />,

    // Defensivas
    'defend': <Shield size={size} className={`action-icon-defend${teamClassSuffix}`} />,
    'block': <Shield size={size} className={`action-icon-block${teamClassSuffix}`} />,
    'parry': <Shield size={size} className={`action-icon-parry${teamClassSuffix}`} />,
    'fortify': <Shield size={size} className={`action-icon-fortify${teamClassSuffix}`} />,

    // Habilidades especiales
    'spell': <Zap size={size} className={`action-icon-spell${teamClassSuffix}`} />,
    'magic': <Zap size={size} className={`action-icon-magic${teamClassSuffix}`} />,
    'heal': <Heart size={size} className={`action-icon-heal${teamClassSuffix}`} />,
    'observe': <Eye size={size} className={`action-icon-observe${teamClassSuffix}`} />,
    'scout': <Eye size={size} className={`action-icon-scout${teamClassSuffix}`} />,

    // Fuego/Proyectiles mágicos
    'fire-projectile': <Flame size={size} className={`action-icon-fire-projectile${teamClassSuffix}`} />,
    'fire-explosion': <Flame size={size} className={`action-icon-fire-explosion${teamClassSuffix}`} />,
    'metafireball': <Flame size={size} className={`action-icon-metafireball${teamClassSuffix}`} />,

    // Psi
    'psi-burn-projectile': <Zap size={size} className={`action-icon-psi-burn${teamClassSuffix}`} />,
    'psi-burn-from-source-projectile': <Zap size={size} className={`action-icon-psi-burn${teamClassSuffix}`} />,
    'amplify-psi': <Zap size={size} className={`action-icon-amplify-psi${teamClassSuffix}`} />,

    // Control
    'taunt': <AlertCircle size={size} className={`action-icon-taunt${teamClassSuffix}`} />,
    'net': <CircleSlash size={size} className={`action-icon-net${teamClassSuffix}`} />,

    // Fallback
    'default': <HelpCircle size={size} className={`action-icon-default${teamClassSuffix}`} />
  };

  // Obtener el icono correspondiente
  const icon = actionIcons[actionSlug] || actionIcons.default;
  
  // Si no hay icono y no queremos fallback, mostrar iniciales
  if (!actionIcons[actionSlug] && !showFallback && action?.name) {
    const initials = action.name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
    return (
      <span className={`action-icon-text ${className}`} style={{ fontSize: `${size * 0.6}px` }}>
        {initials}
      </span>
    );
  }

  return (
    <span className={`action-icon ${className}`} data-action-slug={actionSlug}>
      {icon}
    </span>
  );
};

export default ActionIcon;

